import PdfIcon from "@/assets/pdf-icon";
import { Label } from "@/components/ui/label";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "../error-notification";
import { ExternalLink } from "lucide-react";

type CoverageCertificateProps = {
  base64Pdf: string;
  title?: string;
};

const CoverageCertificate: React.FC<CoverageCertificateProps> = ({
  base64Pdf,
  title = "Group Health Coverage Certificate",
}) => {
  const { addError } = useNotificationError();

  function viewCertificate() {
    try {
      const cleanBase64 = base64Pdf.replace(/\s/g, "");

      if (!cleanBase64.startsWith("JVBERi0")) return;

      const binaryString = atob(cleanBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank");

      // Disable download after 2 min of opening
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (error) {
      addError({
        type: ErrorNotificationType.ERROR,
        message: "Failed to Open Coverage Certificate",
      });
      console.error("PDF open failed:", error);
    }
  }

  return (
    <div className="min-w-68 relative min-h-60 rounded border bg-white p-4 text-center shadow-sm">
      <div className="absolute inset-x-1/2 top-12 flex w-full -translate-x-1/2 transform flex-col items-center justify-center gap-4">
        <div className="max-h-12 max-w-28">
          <PdfIcon className="h-12 w-28 text-primary" />{" "}
        </div>

        <Label className="text-[.875rem] font-semibold text-gray-600">
          {title}
        </Label>
      </div>

      {/*
        2.1.1 Keyboard — A. This was an <a role="button"> with no href: an anchor without
        an href is not focusable, so the control could not be reached or activated by
        keyboard at all — the role attribute only changed how it would have been announced
        had it been reachable. A real <button> is focusable and activates on Enter/Space.
      */}
      <button
        type="button"
        onClick={viewCertificate}
        className="absolute inset-x-1/2 bottom-3 flex min-w-40 -translate-x-1/2 transform items-center justify-center rounded bg-primary py-2 text-sm text-white no-underline hover:bg-primary-selected focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <ExternalLink aria-hidden="true" className="mr-2" size={12} />
        <span>View Document</span>
      </button>
    </div>
  );
};

export default CoverageCertificate;
